import { createContext, useContext } from "react";
import type { ControlProps } from "@jsonforms/core";
import { fieldInputKind, fieldInputOptions } from "../utils/caseFieldPresentation";
import { CASE_REVIEW_FILTER_IDS, CONFIDENCE_FILTERS } from "../constants/caseReviewFilters";
import { REVIEW_DECISIONS } from "../constants/caseReviewDecisions";
import { needsManualEntry as isManualEntry } from "../utils/reviewCounts";
import { hasReviewRecord } from "../utils/caseReviewFieldState";
import { isAcceptable, normalizedValueAvailable } from "../utils/caseReviewAcceptAll";
import { extractedValueNote } from "../utils/extractedValueNote";
import type { CaseReviewContextValue, CaseFieldValue } from "../model/caseReview";
export const CaseReviewContext = createContext<CaseReviewContextValue | null>(null);

export function useCaseReviewContext(): CaseReviewContextValue {
  const context = useContext(CaseReviewContext);
  if (!context) throw new Error("Case Review controls require CaseReviewContext.");
  return context;
}

/** JSON Forms adapter: presentation components know nothing about its update API. */
export function useCaseFieldReview(props: ControlProps) {
  const context = useCaseReviewContext();
  const { path, schema } = props;
  const label = String(schema.title ?? props.label ?? "");
  const evidence = context.evidence[path];
  const decision = context.decisions[path];
  const dirty = context.dirty.includes(path);
  const fieldId = String((schema as Record<string, unknown>)["x-field-id"] ?? path);
  const disabledReason = context.disabledFields?.[fieldId] ?? context.disabledFields?.[path];
  const fieldEditable = context.fieldEditable?.(fieldId, path)
    ?? (!disabledReason && (schema as { readOnly?: boolean }).readOnly !== true);
  const proposal = context.proposalOf?.(fieldId);
  const state = context.fieldStates?.[fieldId];
  // A review row or a proposal: either gives the row something to accept or reject.
  const hasExtractionReview = hasReviewRecord(state);
  const canReview = hasExtractionReview || proposal !== undefined;
  // The extraction's Accept and Restore need an extraction the server could type. A proposal has
  // its own Accept (`proposal.accept`), so it never enables these.
  const canAccept = normalizedValueAvailable(state);
  // Nothing to accept and nobody has decided yet: the reviewer has to enter the value.
  // A saved decision arrives as `decision`, so only a pending, undecided row can be flagged.
  const needsManualEntry = isManualEntry(state, decision, proposal !== undefined);
  const visible = props.visible &&
    (!context.search || label.toLowerCase().includes(context.search.toLowerCase())) &&
    (context.filter !== CASE_REVIEW_FILTER_IDS.unreviewed || ((isAcceptable(state, decision) || (proposal !== undefined && !decision)) && fieldEditable)) &&
    (context.filter !== CASE_REVIEW_FILTER_IDS.manual || (needsManualEntry && fieldEditable)) &&
    (context.filter !== CASE_REVIEW_FILTER_IDS.changed || dirty) &&
    (!CONFIDENCE_FILTERS.includes(context.filter) || evidence?.confidence === context.filter);
  return {
    context,
    showConfidence: state?.normalized_extracted_value_available !== false,
    fieldId,
    displayText: context.displayOverrides?.[fieldId],
    serverError: context.serverErrors?.[fieldId] ?? context.serverErrors?.[path],
    label, evidence, decision, dirty, visible, hasExtractionReview, canReview, canAccept, needsManualEntry, proposal, selected: context.selected === path,
    inputId: `case-review-${context.identityPrefix ? `${context.identityPrefix}-` : ""}${path}`,
    required: !!props.required || !!context.requiredFields?.has(path),
    options: fieldInputOptions(schema as Record<string, unknown>, context.options, props.data),
    disabledReason,
    // A value read from the document but too long to store says so under the field (issue #400).
    valueNote: extractedValueNote(state, decision),
    kind: fieldInputKind(schema as Record<string, unknown>, props.data),
    update: (value: CaseFieldValue) => { context.edit(path); context.changeField(path, value); props.handleChange(path, value); },
    focus: () => context.focus(path), source: () => context.focus(path),
    accept: () => context.decide(path, REVIEW_DECISIONS.accepted),
    reject: () => { context.decide(path, REVIEW_DECISIONS.rejected); props.handleChange(path, null); },
    restoreAiValue: () => {
      const value = context.restoreAiValue(path);
      if (value !== undefined) props.handleChange(path, value);
    },
    revert: () => context.revertDecision(path), undo: () => context.undo(path),
  };
}
